import React, { type ComponentProps, useCallback, useState } from 'react';
import { useNoticeBannersStackContext, useToggleState } from '@sg/garnish';

import { type SubscriptionCreditCard } from '@order/graphql';
import {
  useAddSweetpassPaymentMethod,
  type useSweetpassPlusPlans,
  useSweetpassSubscribe,
} from '@order/hooks';
import { BraintreeCreditCardForm } from '@order/integrations';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { SweetpassSwitchSubscriptionForm } from './SweetpassSwitchSubscriptionForm';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassSwitchSubscriptionContent = (
  props: SweetpassSwitchSubscriptionContentProps,
) => {
  const {
    currentPlanId,
    nextBillingDate,
    plans,
    creditCards,
    isAddingCreditCard,
    termsAndConditions,
    showCreditCardForm,
    hideCreditCardForm,
    onSubscriptionSwitch,
    onRequestClose,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { track } = useTelemetry();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── Mutations ───────────────────────────────────────────────────────

  const { subscribe, isSubscribing: isSwitchingSubscription } =
    useSweetpassSubscribe();
  const { isLoading: isAddingPaymentMethod, addSweetpassPaymentMethod } =
    useAddSweetpassPaymentMethod();

  // ─── State ───────────────────────────────────────────────────────────

  const selectedPlanId =
    plans?.annual?.id === currentPlanId
      ? plans?.monthly?.id
      : plans?.annual?.id;

  const { value: hasAcceptedTerms, toggle: handleAcceptTerms } =
    useToggleState();

  const [selectedCreditCard, selectCreditCard] =
    useState<Partial<SubscriptionCreditCard>>();

  const canSwitchSubscription = Boolean(selectedCreditCard) && hasAcceptedTerms;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleSwitchSubscription = useCallback(async () => {
    const { isSuccess } = await subscribe({
      planId: selectedPlanId ?? '',
      paymentId: selectedCreditCard?.id ?? '',
    });

    if (!isSuccess) {
      track('sweetpass_account.change.failure', {});
      addNoticeBanner({
        id: 'sweetpass-subscription-error',
        text: t('sweetpass.checkout.join-sweetpass-plus.notice.failure'),
        palette: 'caution',
      });
    }

    track('sweetpass_account.change.successful');
    onSubscriptionSwitch?.();
    onRequestClose();
  }, [
    selectedPlanId,
    selectedCreditCard?.id,
    t,
    track,
    addNoticeBanner,
    subscribe,
    onSubscriptionSwitch,
    onRequestClose,
  ]);

  const handleOnAddSweetpassPaymentMethod = useCallback(
    async (
      payload: Parameters<
        ComponentProps<typeof BraintreeCreditCardForm>['handleOnSave']
      >[0],
    ) => {
      return addSweetpassPaymentMethod({
        nonce: payload.id,
        onSuccess: hideCreditCardForm,
      });
    },
    [addSweetpassPaymentMethod, hideCreditCardForm],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (isAddingCreditCard) {
    return (
      <BraintreeCreditCardForm
        vault="subscription"
        excludedFields={['isDefault', 'nickname']}
        isSubmitting={isAddingPaymentMethod}
        handleOnSave={handleOnAddSweetpassPaymentMethod}
      />
    );
  }

  return (
    <SweetpassSwitchSubscriptionForm
      currentPlanId={currentPlanId}
      nextBillingDate={nextBillingDate}
      plans={plans}
      creditCards={creditCards}
      selectedCreditCard={selectedCreditCard}
      hasAcceptedTerms={hasAcceptedTerms}
      canSwitchSubscription={canSwitchSubscription}
      isSwitchingSubscription={isSwitchingSubscription}
      termsAndConditions={termsAndConditions}
      handleAcceptTerms={handleAcceptTerms}
      handleAddCreditCard={showCreditCardForm}
      handleSelectCreditCard={selectCreditCard}
      handleSwitchSubscription={handleSwitchSubscription}
    />
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type SweetpassSwitchSubscriptionContentProps = Readonly<{
  currentPlanId: string;
  nextBillingDate: string;
  plans: ReturnType<typeof useSweetpassPlusPlans>['plans'];
  creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>>;
  isAddingCreditCard: boolean;
  termsAndConditions: string;
  showCreditCardForm: () => void;
  hideCreditCardForm: () => void;
  onSubscriptionSwitch?: () => void;
  onRequestClose: () => void;
}>;
